import { Box, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useInput, type InputProps } from "react-admin";

const optionalHexColor = (value: unknown) => {
  if (value == null || value === "") return undefined;
  if (typeof value !== "string") return "Формат: #RRGGBB";
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return /^#([0-9A-Fa-f]{6})$/.test(trimmed.startsWith("#") ? trimmed : `#${trimmed}`)
    ? undefined
    : "Формат: #RRGGBB";
};

const requiredHexColor = (value: unknown) => {
  if (value == null || value === "" || (typeof value === "string" && !value.trim()))
    return "Обязательное поле";
  return optionalHexColor(value);
};

/** Детские контрастные цвета — те же, что в seed-скрипте фраз */
export const PHRASE_TEXT_PALETTE = [
  "#4A2D7A",
  "#1E3A5F",
  "#1B5E3B",
  "#5C2E6E",
  "#6B3A2A",
  "#0F5C52",
  "#5C4033",
  "#3D3560",
  "#FFFFFF",
  "#FFF8EE",
  "#F5F0FF",
] as const;

function normalizeHex(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  if (/^#([0-9A-Fa-f]{6})$/.test(trimmed)) return trimmed.toUpperCase();
  if (/^([0-9A-Fa-f]{6})$/.test(trimmed)) return `#${trimmed.toUpperCase()}`;
  return fallback;
}

type Props = InputProps & {
  helperText?: string;
  required?: boolean;
};

export const HexColorInput = ({
  source = "TextColor",
  label,
  helperText = "Цвет текста на фоне карточки для контраста",
  defaultValue = "#1A1A1A",
  validate,
  required,
  ...rest
}: Props) => {
  const { field, fieldState, isRequired } = useInput({
    source,
    defaultValue,
    validate: validate ?? (required ? requiredHexColor : optionalHexColor),
    parse: (value) => (typeof value === "string" ? value.trim() : value),
    format: (value) => (value == null ? "" : String(value)),
    ...rest,
  });

  const swatch = normalizeHex(field.value, field.value ? String(field.value) : defaultValue);

  const applyColor = (hex: string) => {
    field.onChange(hex.toUpperCase());
  };

  return (
    <Stack spacing={1.5} sx={{ width: "100%", mb: 1 }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box
          sx={{
            mt: 1,
            width: 48,
            height: 48,
            borderRadius: 1,
            bgcolor: swatch,
            border: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1 }}>
          <TextField
            label={label}
            value={typeof field.value === "string" ? field.value : ""}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={(e) => {
              const normalized = normalizeHex(e.target.value, e.target.value);
              if (normalized !== e.target.value) {
                field.onChange(normalized);
              }
              field.onBlur();
            }}
            required={isRequired || required}
            fullWidth
            error={!!fieldState.error}
            helperText={fieldState.error?.message ?? helperText}
            placeholder="#RRGGBB"
            inputProps={{ spellCheck: false }}
          />
        </Box>
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            Пипетка
          </Typography>
          <Box
            component="label"
            sx={{
              display: "block",
              width: 48,
              height: 32,
              borderRadius: 1,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
              cursor: "pointer",
              "& input": {
                width: "100%",
                height: "100%",
                p: 0,
                border: 0,
                cursor: "pointer",
              },
            }}>
            <input
              type="color"
              value={swatch}
              aria-label="Выбрать цвет"
              onChange={(e) => applyColor(e.target.value)}
            />
          </Box>
        </Box>
      </Stack>

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: "block" }}>
          Палитра
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {PHRASE_TEXT_PALETTE.map((color) => {
            const selected = swatch === color;
            return (
              <IconButton
                key={color}
                size="small"
                aria-label={color}
                onClick={() => applyColor(color)}
                sx={{
                  p: 0,
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  bgcolor: color,
                  border: "2px solid",
                  borderColor: selected ? "primary.main" : "divider",
                  boxShadow: selected ? 2 : 0,
                  "&:hover": { opacity: 0.9, bgcolor: color },
                }}
              />
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
};

export const ColorSwatch = ({ value }: { value?: string }) => {
  if (!value || !/^#([0-9A-Fa-f]{6})$/.test(value)) return <>—</>;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: 0.5,
          bgcolor: value,
          border: "1px solid",
          borderColor: "divider",
        }}
      />
      <Typography variant="body2" component="span">
        {value}
      </Typography>
    </Stack>
  );
};
